import { MemberDataRequest, Role } from '../types/member.types';

import apiClient from '@/axios/app-client';

// API endpoint constant
const STAFFS_SEARCH_ENDPOINT = '/v1/staffs/search';

// Request parameters interface
export interface SearchMembersParams {
  roleKeys?: string[]; // ロールキー
  bucode?: number; // 所属部署コード
  departmentName?: string; // 所属部署名（bucode解決不可時のフォールバック）
  lastName?: string; // 姓
  relationCategories?: number[]; // 担当部署種別
  isDeleted?: boolean; // 削除済みを含む
  sort?: 'createdAt' | 'updatedAt'; // ソート項目
  orderBy?: 'asc' | 'desc'; // ソート順序
  page?: number; // ページ番号
  perPage?: number; // 1ページあたりの件数
  authEmail?: string; // 認証メールアドレス
}

// API Response Staff interface (from backend)
export interface StaffApiResponse {
  id: string;
  firstName: string;
  lastName: string;
  lastNameYomi?: string;
  firstNameYomi?: string;
  tel?: string;
  email: string;
  code: number | null;
  bucode: number | null;
  isSelectable?: boolean | string;
  department?: {
    id: string;
    name: string;
    code: number;
  } | null;
  roles?: Array<{
    role: {
      key: string;
      name: string;
    };
  }>;
  relatedStaffDepartments?: Array<{
    department: {
      id: string;
      name: string;
      code: number;
    };
  }>;
  // Additional flat structure fields from backend API
  authorities?: string[];
  relatedStaffDepartmentCodes?: number[];
  isDeleted?: boolean;
  canUpdate?: boolean;
  createdAt?: string;
  updatedAt?: string;
  createdUser?: string;
  updatedUser?: string;
}

// Pagination info interface
export interface PaginationInfo {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  perPage: number;
}

// Complete API response structure
export interface SearchMembersApiResponse {
  message: string;
  data: StaffApiResponse[];
  pagination: PaginationInfo | null;
}

export const membersService = {
  /**
   * Search members/staffs with pagination
   * @param params - Search parameters
   * @returns Promise with API response data
   */
  searchMembers: async (params: SearchMembersParams = {}): Promise<SearchMembersApiResponse> => {
    const response = await apiClient.get<SearchMembersApiResponse>(STAFFS_SEARCH_ENDPOINT, params);
    // The API client wraps response in ApiResponse format, so we access the nested data
    return response.data.data;
  },

  /**
   * Get staff by ID
   * @param staffId - Staff ID
   * @returns Promise with staff data
   */
  getStaffById: async (staffId: string): Promise<StaffApiResponse> => {
    const response = await apiClient.get<StaffApiResponse>(`/v1/staffs/${staffId}`);
    return response.data.data;
  },
  /**
   * Get all roles
   * @returns Promise with array of roles
   */
  getRoles: async (): Promise<Role[]> => {
    const response = await apiClient.get<Role[]>('/v1/roles');
    return response.data.data;
  },

  /**
   * Create a new member/staff
   * @param data - Member creation data
   * @returns Promise<void>
   */
  createMember: async (data: MemberDataRequest): Promise<void> => {
    await apiClient.post('/v1/staffs', data);
  },

  /**
   * Update staff by ID
   * @param staffId - Staff ID
   * @param data - Member update data
   * @returns Promise with updated staff data
   */
  updateStaffById: async (staffId: string, data: MemberDataRequest): Promise<StaffApiResponse> => {
    const response = await apiClient.put<StaffApiResponse>(`/v1/staffs/${staffId}`, data);
    return response.data.data;
  },

  /**
   * Delete a member/staff by ID
   * @param id - Member ID
   * @returns Promise<void>
   */
  deleteMember: async (id: string): Promise<void> => {
    await apiClient.delete(`/v1/staffs/${id}`);
  },
  /**   * Delete multiple members/staffs by IDs
   * @param ids - Array of Member IDs
   * @returns Promise<void>
   */
  deleteMembers: async (ids: string[]): Promise<void> => {
    await apiClient.put(`/v1/staffs/delete`, {
      ids: ids,
    });
  },
  /**   * Restore a deleted member/staff by ID
   * @param id - Member ID
   * @returns Promise<void>
   */
  restoreMember: async (id: string): Promise<void> => {
    await apiClient.put(`/v1/staffs/restore/${id}`);
  },
  /**
   * Reinvite a user by email using OIDC API
   * @param email - User email address
   * @returns Promise with reinvite result
   */
  reinviteMember: async (email: string): Promise<void> => {
    await apiClient.post('/v1/oidc-api/users/reinvite', {
      email,
    });
  },
  /**
   * Get staff by auth email
   * @param authEmail - Auth email
   * @returns Promise with staff data
   */
  getUserAuthInfoByToken: async (): Promise<StaffApiResponse> => {
    const response = await apiClient.get<StaffApiResponse>(`/v1/staffs/auth`);
    return response.data.data;
  },
};
